import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
  ifDefined,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import {
  analyticsCtaDataObject,
  analyticsLabelFromString,
  handleAdobeNavEvent,
} from '../../../helpers/adobe-analytics';

const DESKTOP_MAX = 9;
const TABLET_MAX = 6;
const MOBILE_MAX = 3;

export class Silvy extends Lit3formBlock {
  static get characterName() {
    return 'silvy';
  }

  static get characterStyles() {
    return [...styles];
  }

  render() {
    return html`
      <div class="block-wrapper" style=${this.perRowStyle}>
        <div class="block-grid silvy-layout">
          ${this.headerHTML} ${this.imagesHTML} ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get perRowStyle() {
    const chosen = Number(this.content.images_per_row) || DESKTOP_MAX;
    const desktop = Math.min(chosen, DESKTOP_MAX);
    const tablet = Math.min(chosen, TABLET_MAX);
    const mobile = Math.min(chosen, MOBILE_MAX);
    return `--silvy-per-row-desktop:${desktop};--silvy-per-row-tablet:${tablet};--silvy-per-row-mobile:${mobile};`;
  }

  get headerHTML() {
    if (!this.headerText && !this.descriptionText && !this.ctaData) {
      return html``;
    }
    return html`
      <block-intro
        class="intro"
        header=${this.content.header || ''}
        description=${this.content.description || ''}
      >
        ${this.ctaHTML}
      </block-intro>
    `;
  }

  get ctaHTML() {
    const cta = this.ctaData;
    if (!cta || !cta.text) return html``;
    return html`
      <cta-text
        class="learn-more"
        cta-data=${JSON.stringify(cta)}
        .ctaAnalyticsData=${analyticsCtaDataObject(cta, this.headerText)}
      ></cta-text>
    `;
  }

  get ctaData() {
    return this.content.call_to_action;
  }

  get imagesHTML() {
    if (!this.imagesArr.length) return html``;
    return html`
      <div class="${classMap(this.imagesClasses)}">
        ${this.imagesArr.map(i => this.imageHTML(i[1]))}
      </div>
    `;
  }

  get imagesClasses() {
    return {
      images: true,
      'images--native': this.usesSampleCatalog,
    };
  }

  // Auto-populated swatches are shown uncropped, so the 1:1 lock that applies
  // to hand-picked driver card images is lifted.
  get usesSampleCatalog() {
    return !!this.content.sample_type_slug;
  }

  imageHTML(image) {
    const img = this.imgHTML(image);
    return image.url
      ? html`
          <a
            class="image"
            href=${image.url}
            @click="${event => this.handleImageClick(image, event)}"
          >
            ${img}
          </a>
        `
      : html`
          <div class="image">
            ${img}
          </div>
        `;
  }

  imgHTML(image) {
    // Intrinsic dimensions let the browser reserve aspect-ratio space before
    // the image arrives. Only meaningful uncropped: the square variant is 1:1
    // whatever the source measures.
    const native = this.usesSampleCatalog;
    return html`
      <img
        src=${this.imageUrl(image)}
        alt=${image.alt_text || ''}
        width=${ifDefined((native && image.width) || undefined)}
        height=${ifDefined((native && image.height) || undefined)}
      />
    `;
  }

  handleImageClick(image, event) {
    handleAdobeNavEvent({
      navString: `NAV|${analyticsLabelFromString(
        this.headerText || 'Silvy'
      )}|${analyticsLabelFromString(image.alt_text || image.url)}`,
      destinationUrl: image.url,
      event,
    });
  }

  imageUrl(image) {
    // Passing no height makes ImageBoss scale to width instead of centre
    // cropping to a square.
    return this.usesSampleCatalog
      ? this.imagebossUrl(image.src, 400)
      : this.imagebossUrl(image.src, 400, 400);
  }
}
Silvy.defineElement();
